import React, { useState, useMemo } from "react";
import {
  Box,
  InlineStack,
  Text,
  Avatar,
  Icon,
  Badge,
  Tooltip,
  Popover,
  ActionList,
  Button,
  Toast,
  Thumbnail,
  Modal,
  Divider,
  BlockStack,
  FormLayout,
} from "@shopify/polaris";
import { ArrowRightIcon, DeleteIcon, ChatIcon } from "@shopify/polaris-icons";
import axios from "../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import TaskSlider from "../TaskSlider";

export default function TaskCard({
  task,
  dragListeners,
  dragAttributes,
  statuses,
  onStatusChanged,
  onLocalMove,
  onOpenSlider,
  onRefresh,
  onOpenChat,
}) {
  console.log("task=", task);
  const [popoverActive, setPopoverActive] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const toggleToastActive = () => setToastActive((a) => !a);

  console.log("task=", task);
  const handleMove = async (status, id) => {
    const oldStatus = task.status;
    if (onLocalMove) onLocalMove(id, status.name); // ✅ move instantly in UI
    try {
      const response = await axios.patch(`/task-managers/${id}/status`, {
        status_id: status.id,
      });
      // console.log("✅ Moved task", taskId, "to", status.name, response.data);
      setToastMessage(`Moved to ${status.name}`);
      setToastActive(true);
      if (onStatusChanged) onStatusChanged();
      // optional callback for parent to refresh data
      //if (onMoved) onMoved(status.id);
    } catch (error) {
      console.error("❌ Failed to move task:", error);
      setToastMessage("Failed to move task");
      setToastActive(true);
      if (onLocalMove) onLocalMove(id, oldStatus);
    }
  };
  const { id } = useParams();
  const navigate = useNavigate();
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const toggleDeleteModal = () => setDeleteModalActive((a) => !a);
  const handleDelete = async () => {
    try {
      await axios.delete(`/task-manager/${task.id}`);
      setToastMessage("Task deleted successfully");
      setToastActive(true);
      setDeleteModalActive(false);
      onRefresh();
    } catch (error) {
      console.error("❌ Failed to delete task:", error);
      setToastMessage("Failed to delete task");
      setToastActive(true);
      setDeleteModalActive(false);
    }
  };
  const [showSlider, setShowSlider] = useState(false);
  function daysFromToday(dateString) {
    if (!dateString) return null; // ✅ No deadline provided

    const target = new Date(dateString);
    if (isNaN(target.getTime())) return null; // ✅ Invalid date check

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);

    const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  const closeSlider = () => {
    setShowSlider(false);
  };
  const pastelColors = [
    "#FADADD", // pink
    "#F9E79F", // yellow
    "#A3E4D7", // teal
    "#AED6F1", // blue
    "#D7BDE2", // purple
    "#F5CBA7", // peach
    "#ABEBC6", // mint
    "#FAD7A0", // light orange
  ];

  // 🔹 Generate consistent color for each name
  function getColorForName(name) {
    if (!name) return pastelColors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % pastelColors.length;
    return pastelColors[index];
  }
  const bgColor = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * pastelColors.length);
    return pastelColors[randomIndex];
  }, []);
  return (
    <Box
      borderRadius="100"
      padding="200"
      shadow="100"
      hover="bg-surface-hover"
      position="relative"
      style={{
        overflow: "visible",
        backgroundColor: String(task?.is_high) === "1" && "#fde5e5",
        padding: "0.3rem",
      }}
    >
      <InlineStack align="space-between" blockAlign="center">
        {/* ✅ Left + right grouped in one line */}
        <div
          {...dragAttributes}
          {...dragListeners}
          style={{
            cursor: "grab",
            userSelect: "none",
            flex: 1, // allows right side to align naturally
          }}
        >
          <InlineStack align="space-between" blockAlign="center">
            {/* Title */}
            <InlineStack gap="100" blockAlign="center">
              <div
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenSlider(task); // ✅ tell parent to open/update slider
                }}
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <Text variant="bodyMd" fontWeight="medium">
                  {task.title}
                </Text>
              </div>
            </InlineStack>

            {/* Right side info (except arrow) */}
            <InlineStack gap="300" blockAlign="center">
              {/* Screenshots */}
              <InlineStack gap="100">
                {task?.screenshots?.map((img, i) => (
                  <Tooltip
                    key={i}
                    preferredPosition="above"
                    dismissOnMouseOut
                    content={
                      <img
                        src={img}
                        alt={`screenshot-${i}`}
                        style={{
                          width: "200px",
                          height: "auto",
                          borderRadius: "8px",
                          boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                        }}
                      />
                    }
                  >
                    <div
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        userSelect: "none",
                        pointerEvents: "auto",
                        cursor: "pointer",
                      }}
                      onClick={() => window.open(img, "_blank")}
                    >
                      <Thumbnail
                        size="extraSmall"
                        source={img}
                        alt={`screenshot-${i}`}
                      />
                    </div>
                  </Tooltip>
                ))}
              </InlineStack>

              {/* Divider */}
              <Box
                style={{
                  height: "28px",
                  borderInlineStart: "1px solid #dae0e7",
                }}
              />

              {/* Avatars */}
              <InlineStack gap="100">
                {[...task.assignees, ...task.reporters].map((m, i) => (
                  <Tooltip
                    dismissOnMouseOut
                    key={i}
                    content={
                      <div className="avatar-tooltip">
                        <Text variant="bodySm" as="p">
                          <b>
                            {i < task.assignees.length
                              ? "Assignee"
                              : "Reporter"}
                          </b>
                        </Text>
                        <Text variant="bodySm" as="p">
                          {m.name}
                        </Text>
                      </div>
                    }
                    preferredPosition="above"
                  >
                    {/* {i < task.assignees.length ? ( */}
                    <div
                      className="avatar-wrapper"
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        userSelect: "none",
                        pointerEvents: "auto",
                        cursor: "pointer",
                      }}
                    >
                      {i < task.assignees.length ? (
                        <img
                          src={m.image_url}
                          alt={m.name}
                          className="avatar-img"
                          style={{
                            border: `2px solid #b7e9d8`,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            border: "2px solid #f0f2f4",
                            width: "28px",
                            height: "28px",
                            borderRadius: "50%",
                            backgroundColor: getColorForName(m.name),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#333",
                            fontWeight: 600,
                            fontSize: "0.6rem",
                            lineHeight: "1", // ✅ ensure vertical centering
                            textTransform: "uppercase",
                            userSelect: "none",
                            cursor: "pointer",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                          }}
                        >
                          {m.name
                            ?.split(" ")
                            .filter(Boolean)
                            .map((word) => word[0].toUpperCase())
                            .slice(0, 2)
                            .join("")}
                        </div>
                      )}

                      <div
                        className="avatar-initial"
                        style={{
                          backgroundColor: `${
                            i < task.assignees.length ? "#f0f2f4" : "#eab308"
                          }`,
                          color: `${
                            i < task.assignees.length ? "#79879a" : "white"
                          }`,
                        }}
                      >
                        {i < task.assignees.length ? "A" : "R"}
                        {/* {m.name?.charAt(0)?.toUpperCase()} */}
                      </div>
                    </div>
                    {/* ) : (
                      <div
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                          border: `2px solid  "#f0f2f4"`,
                          userSelect: "none",
                          pointerEvents: "auto",
                          cursor: "pointer",
                          width: "28px",
                          height: "28px",
                          borderRadius: "50%",
                          backgroundColor: bgColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#333",
                          // fontWeight: "600",
                          fontSize: "0.6rem",
                          textTransform: "uppercase",
                          userSelect: "none",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                      >
                        {m.name
                          ?.split(" ")
                          .filter(Boolean)
                          .map((word) => word[0].toUpperCase())
                          .slice(0, 2)
                          .join("")}
                      </div>
                    )} */}
                  </Tooltip>
                ))}
              </InlineStack>

              {/* Divider */}
              <Box
                style={{
                  height: "28px",
                  borderInlineStart: "1px solid #dae0e7",
                }}
              />

              {/* Deadline badge */}
              {daysFromToday(task.deadline) !== null ? (
                <Tooltip
                  content={
                    daysFromToday(task.deadline) < 0
                      ? `Overdue by ${Math.abs(
                          daysFromToday(task.deadline),
                        )} days`
                      : `${daysFromToday(task.deadline)} days left`
                  }
                  dismissOnMouseOut
                >
                  <div
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                      userSelect: "none",
                      pointerEvents: "auto",
                      cursor: "pointer",
                    }}
                  >
                    <Badge
                      tone={
                        daysFromToday(task.deadline) < 0
                          ? "critical"
                          : daysFromToday(task.deadline) < 10
                            ? "warning"
                            : "default"
                      }
                      // tone={
                      //   daysFromToday(task.deadline) < 0
                      //     ? "critical"
                      //     : daysFromToday(task.deadline) <= 3
                      //     ? "attention"
                      //     : "success"
                      // }
                    >
                      {daysFromToday(task.deadline)}d
                    </Badge>
                  </div>
                </Tooltip>
              ) : (
                <Badge tone="subdued">NA</Badge>
              )}

              {/* Divider */}
              <Box
                style={{
                  height: "28px",
                  borderInlineStart: "1px solid #dae0e7",
                }}
              />

              {/* Action icons */}
              <InlineStack gap="200" blockAlign="center">
                {/* <div
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();

                    // Option A (recommended): open the same slider you already have
                    onOpenChat(task);
                    //onOpenSlider(task);

                    // Option B: navigate somewhere (if you have a chat route)
                    // navigate(`/admin/task-manager/projects/${id}/tasks/${task.id}?tab=chat`);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Tooltip content="Chat" dismissOnMouseOut>
                    <Icon source={ChatIcon} />
                  </Tooltip>
                </div> */}

                {/* ✅ Delete icon (clickable) */}
                <div
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteModalActive(true);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Tooltip content="Delete" dismissOnMouseOut>
                    <Icon source={DeleteIcon} />
                  </Tooltip>
                </div>

                {/* ✅ Popover activator icon (clickable) */}
                {/* ✅ Popover activator icon (clickable, isolated from drag) */}
                <Box
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    pointerEvents: "auto",
                    cursor: "pointer",
                    position: "relative",
                  }}
                >
                  <Tooltip content="Move" dismissOnMouseOut>
                    <Popover
                      active={popoverActive}
                      onClose={() => setPopoverActive(false)}
                      zIndexOverride={9999}
                      activator={
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setPopoverActive((a) => !a);
                          }}
                        >
                          <Icon source={ArrowRightIcon} />
                        </div>
                      }
                    >
                      <ActionList
                        items={statuses?.map((status) => ({
                          content: `Move to ${status.name}`,
                          onAction: () => {
                            setPopoverActive(false); // close immediately
                            handleMove(status, task.id);
                          },
                        }))}
                      />
                    </Popover>
                  </Tooltip>
                </Box>
              </InlineStack>
            </InlineStack>
          </InlineStack>
        </div>
        {/* <div
          onMouseDown={(e) => e.stopPropagation()} // prevent drag start
          onClick={(e) => {
            e.stopPropagation();
            setDeleteModalActive(true);
          }}
          style={{ cursor: "pointer" }}
        >
          <Icon source={DeleteIcon} />
        </div> */}
        {/* <div
          onMouseDown={(e) => e.stopPropagation()} // prevent drag start
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/task-manager/projects/${id}/tasks/${task.id}`);
          }}
          style={{ cursor: "pointer" }}
        >
          <Icon source={EditIcon} />
        </div>
        <div
          onMouseDown={(e) => e.stopPropagation()} // prevent drag start
          onClick={(e) => {
            e.stopPropagation();
            setPopoverActive((a) => !a);
          }}
          style={{ marginLeft: "8px" }}
        >
          <Popover
            active={popoverActive}
            activator={
              <div style={{ cursor: "pointer" }}>
                <Icon source={ArrowRightIcon} />
              </div>
            }
            onClose={() => setPopoverActive(false)}
            zIndexOverride={9999}
          >
            <ActionList
              items={statuses?.map((status) => ({
                content: `Move to ${status.name}`,
                onAction: () => handleMove(status, task.id),
              }))}
            />
            {toastActive && (
              <Toast content={toastMessage} onDismiss={toggleToastActive} />
            )}
          </Popover>
        </div> */}
      </InlineStack>
      <Modal
        open={deleteModalActive}
        onClose={toggleDeleteModal}
        title="Confirm delete"
        primaryAction={{
          content: "Yes",
          destructive: true,
          onAction: handleDelete,
        }}
        secondaryActions={[
          {
            content: "No",
            onAction: toggleDeleteModal,
          },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this task?</Text>
        </Modal.Section>
      </Modal>

      {/* ✅ Right slide panel */}
      {/* {showSlider && <TaskSlider task={task} closeSlider={closeSlider} />} */}
    </Box>
  );
}
